import { Head, usePage } from "@inertiajs/react"
import { Download } from "lucide-react"

import { StaffLayout } from "@/components/layouts/staff-layout"
import { Button } from "@/components/ui/button"
import { MainContainer } from "@/components/ui/container"
import { PageHeader } from "@/components/ui/headers"
import { NuqsInertiaAdapter } from "@/features/staff/nuqs-inertia-adapter"
import { ReportCaptureBlock } from "@/features/staff/reports/capture-block"
import { ReportControlBlock } from "@/features/staff/reports/control-block"
import { ReportEmptyState } from "@/features/staff/reports/empty-state"
import { ReportForecastBlock } from "@/features/staff/reports/forecast-block"
import { ReportMixBlock } from "@/features/staff/reports/mix-block"
import { ReportPeriodBar } from "@/features/staff/reports/period-bar"
import { ReportSalesBlock } from "@/features/staff/reports/sales-block"
import { tReports, tSharedReports } from "@/features/staff/reports/format"
import type { ReportsPageProps } from "@/features/staff/reports/types"

/**
 * Reports.
 *
 * Read by the owner, on a phone, from home, wanting to know how Saturday is
 * going. That sentence decided the whole layout: `grid-cols-2` is the base and
 * `sm:grid-cols-3` the widening, the container is the narrow one the till uses,
 * the period chips take the full 44px target, and there is not one horizontal
 * scroll anywhere. Desktop is the reduction, not the design.
 *
 * Five reports, one screen, in the order the questions get asked:
 *
 *   Ventas del periodo   the headline, and the ONLY figure on this page that
 *                        reconciles with the signed Z reports inside it.
 *   Pronóstico           what the model expects next, right beside the total
 *                        it grew out of — never the only carrier of a figure,
 *                        and never drawn without saying which method and how
 *                        wide the band is. Can vanish entirely when history
 *                        supports nothing yet.
 *   Mix de productos     what sold, and which dishes carry the menu.
 *   Control              discounts, cancellations, refunds and reopenings, each
 *                        with an actor and a reason. The differentiator.
 *   Capturado por        who rang it up, with self-service as its own row.
 *
 * The last three deliberately do not add up to the first, because they count
 * different things, and each one says so on its own card rather than leaving the
 * reader to discover it. The forecast does not add up to it either — it is an
 * estimate of a DIFFERENT window, the one still ahead.
 *
 * It sits beside `/shifts` rather than under `/manage` for the reason the route
 * file gives: a report configures nothing. The controller carries its own
 * manager predicate, so the placement costs no access control.
 *
 * Everything here is composition. Every figure was computed server-side in
 * integer cents and arrives as an exact decimal string; this page formats and
 * arranges, and derives nothing.
 */

export default function StaffLocationReportsShow() {
  const {
    report,
    periods = [],
    exportPath,
    hasClosedShifts = true,
  } = usePage<ReportsPageProps>().props

  const { period, sales, mix, control, capture, forecast } = report

  return (
    // The adapter has to sit above anything reading the period, and it is
    // mounted here rather than in the shared entry point so the one screen using
    // nuqs is the only one paying for it.
    <NuqsInertiaAdapter>
      <Head title={tSharedReports("title")} />

      {/* `md`, the operate-surface measure every staff working screen shares.
          The Container-Width Rule puts a report here, not on `sm`.

          The rule the `sm` container used to enforce is still true, though: a
          column of amounts stretched to 1280px is a column nobody can scan. So
          width is spent on a SECOND column rather than on wider rows —
          `max-w-xl` below `xl` keeps the till's measure at every size where
          only one column fits, and each bento column lands at ~600px, which is
          the measure this screen already had. */}
      <MainContainer size="md">
        <div className="mx-auto max-w-xl space-y-4 xl:max-w-none" data-testid="reports-screen">
          <PageHeader
            title={tSharedReports("title")}
            description={tReports("description")}
            actions={
              // A file download, so a plain anchor: an Inertia visit would ask
              // for JSON and get a CSV. Hidden when the period has no close in
              // it, because an empty export is a dead control.
              period.empty ? undefined : (
                <Button asChild variant="secondary">
                  <a href={exportPath} data-testid="report-export-csv">
                    <Download />
                    {tSharedReports("export")}
                  </a>
                </Button>
              )
            }
          />

          <ReportPeriodBar period={period} presets={periods} />

          {period.empty ? (
            <ReportEmptyState hasClosedShifts={hasClosedShifts} />
          ) : (
            /* A bento of two columns, not a masonry.

               The cards have wildly unequal heights — control is on its own
               roughly as tall as the other four together — so auto-placing
               five cards into `grid-cols-2` would size each row to its taller
               card and leave exactly the hole this layout exists to close.
               Each column is therefore its own stack and the split is made
               here, by hand, against measured heights.

               It is not `columns-2` either: CSS multi-column rebalances on
               every content change, so which card sits where would depend on
               how many discount reasons a restaurant happened to use that
               month. A report is read in a fixed order and has to stay put.

               Left is what the period SOLD — takings, what is coming, which
               dishes carried it. Right is who ACCOUNTED for it — the exception
               log, then the attribution of every line to the person who rang
               it. The split reads as two questions rather than as four cards
               and one leftover.

               It also happens to balance: measured at 1440px the left stack is
               2956px against control-plus-capture's 3042px, so neither column
               trails the other by a card. Keep that in mind before moving a
               card across — the reason capture sits under control is that it
               is what makes the two sides come out level.

               Below `xl` this collapses to one stack, and the DOM order here is
               deliberately the order that screen already had: sales, forecast,
               mix, control, capture. Mobile is untouched. */
            <div className="space-y-4 xl:grid xl:grid-cols-2 xl:items-start xl:gap-4 xl:space-y-0">
              <div className="space-y-4">
                <ReportSalesBlock sales={sales} />
                <ReportForecastBlock forecast={forecast} byDay={sales.byDay} />
                <ReportMixBlock mix={mix} />
              </div>

              <div className="space-y-4">
                <ReportControlBlock control={control} />
                <ReportCaptureBlock capture={capture} />
              </div>
            </div>
          )}
        </div>
      </MainContainer>
    </NuqsInertiaAdapter>
  )
}

StaffLocationReportsShow.layout = (page: React.ReactNode) => <StaffLayout>{page}</StaffLayout>
